import recipes from './soundboardRecipes';

const levenshteinDistance = (str1, str2) => {
  const matrix = [];

  for (let i = 0; i <= str2.length; i++) {
    matrix[i] = [i];
  }

  for (let j = 0; j <= str1.length; j++) {
    matrix[0][j] = j;
  }

  for (let i = 1; i <= str2.length; i++) {
    for (let j = 1; j <= str1.length; j++) {
      if (str2.charAt(i - 1) === str1.charAt(j - 1)) {
        matrix[i][j] = matrix[i - 1][j - 1];
      } else {
        matrix[i][j] = Math.min(
          matrix[i - 1][j - 1] + 1,
          matrix[i][j - 1] + 1,
          matrix[i - 1][j] + 1
        );
      }
    }
  }

  return matrix[str2.length][str1.length];
};

export const calculateSimilarity = (str1, str2) => {
  const s1 = str1.toLowerCase().trim();
  const s2 = str2.toLowerCase().trim();

  if (s1 === s2) return 1.0;
  if (s1.includes(s2) || s2.includes(s1)) return 0.95;

  const longer = s1.length > s2.length ? s1 : s2;

  if (longer.length === 0) return 1.0;

  const editDistance = levenshteinDistance(s1, s2);
  const similarity = (longer.length - editDistance) / longer.length;

  const words1 = s1.split(/\s+/);
  const words2 = s2.split(/\s+/);
  let wordMatches = 0;

  for (const w1 of words1) {
    for (const w2 of words2) {
      if (w1 === w2 || w1.includes(w2) || w2.includes(w1)) {
        wordMatches++;
        break;
      }
    }
  }

  const wordSimilarity = wordMatches / Math.max(words1.length, words2.length);
  return Math.max(similarity, wordSimilarity);
};

export const findRecipe = (name1, name2, elements) => {
  const SIMILARITY_THRESHOLD = 0.5;

  let bestMatch = null;
  let bestScore = 0;

  for (const recipe of recipes) {
    const ing1 = recipe.ingredients[0];
    const ing2 = recipe.ingredients[1];

    const score1a = calculateSimilarity(name1, ing1);
    const score1b = calculateSimilarity(name2, ing2);
    const score1 = (score1a + score1b) / 2;

    const score2a = calculateSimilarity(name1, ing2);
    const score2b = calculateSimilarity(name2, ing1);
    const score2 = (score2a + score2b) / 2;

    const maxScore = Math.max(score1, score2);

    if (maxScore >= SIMILARITY_THRESHOLD && maxScore > bestScore) {
      bestScore = maxScore;
      bestMatch = recipe.result;
    }
  }

  if (!bestMatch) {
    // 🔥 FIX: Create a short, clean name
    const cleanName1 = name1.substring(0, Math.min(4, name1.length));
    const cleanName2 = name2.substring(0, Math.min(4, name2.length));
    const combinedName = cleanName1 + cleanName2;

    // 🔥 FIX: Extract ONLY the first emoji character (not compound emojis)
    const firstItem = elements.find(el => el.name === name1);
    let singleEmoji = '❓';

    if (firstItem && firstItem.emoji) {
      // 🔥 Get only the first emoji using regex or array spread
      const emojiMatch = firstItem.emoji.match(/\p{Emoji}/u);
      singleEmoji = emojiMatch ? emojiMatch[0] : firstItem.emoji.charAt(0);
    }

    return {
      name: combinedName,
      emoji: singleEmoji, // 🔥 GUARANTEED single emoji
      level: 1
    };
  }

  // 🔥 IMPORTANT: Ensure recipe results also have single emoji
  return {
    name: bestMatch.name,
    emoji: bestMatch.emoji, // This should already be single from recipes array
    level: bestMatch.level
  };
};

export const BASE_ELEMENTS = [
  { id: 1, name: 'Ball', emoji: '⚽', isBase: true },
  { id: 2, name: 'Bat', emoji: '🏏', isBase: true },
  { id: 3, name: 'Glove', emoji: '🧤', isBase: true },
  { id: 4, name: 'Shoe', emoji: '👟', isBase: true },
  { id: 5, name: 'Whistle', emoji: '📢', isBase: true },
  { id: 6, name: 'Net', emoji: '🥅', isBase: true },
  { id: 7, name: 'Medal', emoji: '🏅', isBase: true },
  { id: 8, name: 'Stadium', emoji: '🏟️', isBase: true },
  { id: 9, name: 'Trophy', emoji: '🏆', isBase: true },
  { id: 10, name: 'Field', emoji: '🌾', isBase: true }
];
